import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { ScheduleType } from 'webapp/enums/enums';

import FormSelectComponent from 'webapp/components/form/select';
import NotificationsEditFormRowComponent from 'webapp/components/notifications/edit/form/row';

import type Schedule from 'webapp/models-custom/schedule';

interface ModelWithSchedule {
	schedule: Schedule;
	scheduleType: number;
	scheduleHourOfDay: number | null;
	scheduleDayOfWeek: number[];
	scheduleDayOfMonth: number | null;
	updateScheduleValues: () => void;
}

interface Args {
	model: ModelWithSchedule;
	showOneOff: boolean;
	submitInProgress: boolean;
	labelText?: string;
	theme?: string;
}

export default class FormScheduleComponent extends Component<Args> {
	@service('constants') declare constantsService: Service.Constants;

	get labelText(): string {
		return this.args.labelText || 'Frequency';
	}

	get scheduleItemsShown(): number {
		let itemsShown = 1;

		const schedule = this.args.model.schedule;
		if (schedule.hasDayOfMonth || schedule.hasDayOfWeek) {
			itemsShown++;
		}
		if (schedule.hasHourOfDay) {
			itemsShown++;
		}
		return itemsShown;
	}

	get scheduleDayOfWeek(): string[] {
		return this.args.model.scheduleDayOfWeek.map((i) => String(i));
	}

	get frequencyValues(): LabelValuePairNumeric[] {
		const allowedScheduleTypes = [
			ScheduleType.DAILY,
			ScheduleType.WEEKLY,
			ScheduleType.MONTHLY,
			ScheduleType.QUARTERLY,
		];

		if (this.args.showOneOff) {
			allowedScheduleTypes.push(ScheduleType.ONE_OFF);
		}

		return this.constantsService.scheduleTypes.filter((scheduleType) => {
			return allowedScheduleTypes.includes(scheduleType.value);
		});
	}

	@action
	changeFrequency(value: number): void {
		this.args.model.scheduleType = value;
		this.args.model.updateScheduleValues();
	}

	@action
	changeDaysOfWeek(value: string[]): void {
		this.args.model.scheduleDayOfWeek = value.map((i) => Number(i));
		this.args.model.updateScheduleValues();
	}

	@action
	changeDayOfMonth(value: number): void {
		this.args.model.scheduleDayOfMonth = value;
	}

	@action
	changeHourOfDay(value: number): void {
		this.args.model.scheduleHourOfDay = value;
	}
	<template>
		<NotificationsEditFormRowComponent @label="Frequency" @className="row--dropdowns-in-line">
			<div class="selector">
				<FormSelectComponent
					name="frequency"
					@selected={{@model.scheduleType}}
					@options={{this.frequencyValues}}
					@onChange={{this.changeFrequency}}
					@isDisabled={{@submitInProgress}}
					@tooltipOnSelectionFromProperty=""
					@showFloatlabel={{false}}
					@isRequired={{true}}
					@theme={{@theme}}
				/>
			</div>

			{{#if @model.schedule.hasDayOfWeek}}
				<div class="selector">
					<label>Every</label>
					<FormSelectComponent
						name="day"
						@options={{this.constantsService.daysOfWeekStrings}}
						@selected={{this.scheduleDayOfWeek}}
						@tooltipOnSelectionFromProperty=""
						@showFloatlabel={{false}}
						@isRequired={{true}}
						@isDisabled={{@submitInProgress}}
						@isNumeric={{false}}
						@isMultiple={{true}}
						@maxTagsShown={{1}}
						@onChange={{this.changeDaysOfWeek}}
						@theme={{@theme}}
					/>
				</div>
			{{else if @model.schedule.hasDayOfMonth}}
				<div class="selector">
					<label>Every</label>
					<FormSelectComponent
						name="day"
						@selected={{@model.scheduleDayOfMonth}}
						@options={{@model.schedule.valuesForDayOfMonth}}
						@onChange={{this.changeDayOfMonth}}
						@isDisabled={{@submitInProgress}}
						@tooltipOnSelectionFromProperty=""
						@showFloatlabel={{false}}
						@isRequired={{true}}
						@theme={{@theme}}
					/>
				</div>
			{{/if}}

			{{#if @model.schedule.hasHourOfDay}}
				<div class="selector">
					<label>At</label>
					<FormSelectComponent
						name="time"
						@selected={{@model.scheduleHourOfDay}}
						@options={{this.constantsService.hoursOfDay}}
						@onChange={{this.changeHourOfDay}}
						@isDisabled={{@submitInProgress}}
						@tooltipOnSelectionFromProperty=""
						@showFloatlabel={{false}}
						@isRequired={{true}}
						@theme={{@theme}}
					/>
				</div>
			{{/if}}
		</NotificationsEditFormRowComponent>
	</template>
}
